import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  openErrorNotification,
  openSuccessNotification,
} from '../../utils/openNotification'
import { AUTH_TOKEN } from 'constants/AuthConstant'
import i18n from 'lang'
import IdentityService from 'services/IdentityService'
import jwtDecode from 'jwt-decode'

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',
  token: localStorage.getItem(AUTH_TOKEN) || null,
  me: null,
  roles: null,
  changePasswordIsLoading: false,
  updateMeIsLoading: false,
  resendVerifyIsLoading: false,
}

export const signIn = createAsyncThunk(
  'identity/login',
  async (data, { rejectWithValue, dispatch }) => {
    // TODO implement remember feature
    const { email, password, remember } = data

    let refresh_token = undefined

    if (remember) {
      refresh_token = 'cookie'
    }

    try {
      const response = await IdentityService.login({
        email,
        password,
        refresh_token,
      })
      const token = response.access_token
      localStorage.setItem(AUTH_TOKEN, token)

      dispatch(getMe())

      return token
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const signUp = createAsyncThunk(
  'identity/register',
  async (data, { rejectWithValue, dispatch }) => {
    const { email, name, username, password, referral_id, cpf, date_of_birth } =
      data

    try {
      await IdentityService.register({
        email,
        name,
        username,
        password,
        referral_id,
        cpf,
        date_of_birth,
      })
      const response = await IdentityService.login({ email, password })

      dispatch(getMe())

      const token = response.access_token
      localStorage.setItem(AUTH_TOKEN, token)
      return token
    } catch (err) {
      if (err.response?.data?.details?.length > 0) {
        return rejectWithValue(err.response?.data?.details[0].message)
      } else if (err.response?.data?.error) {
        return rejectWithValue(err.response?.data?.error)
      } else {
        return rejectWithValue('Error')
      }
    }
  }
)

export const signOut = createAsyncThunk('identity/logout', async () => {
  const access_token = localStorage.getItem(AUTH_TOKEN)

  await IdentityService.logout({ access_token })
  localStorage.removeItem(AUTH_TOKEN)

  return Promise.resolve()
})

export const getMe = createAsyncThunk('identity/me', async () => {
  const response = await IdentityService.getMe()

  return response
})

export const webNotifyToken = createAsyncThunk(
  'identity/webNotifyToken',
  async () => {
    const response = await IdentityService.webNotifyToken()

    return response
  }
)

export const updateMe = createAsyncThunk(
  'identity/updateMe',
  async (data, { rejectWithValue }) => {
    const { user, phone, address } = data

    try {
      return await IdentityService.updateMe(user, phone, address)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const changePassword = createAsyncThunk(
  'identity/changePassword',
  async (data, { rejectWithValue }) => {
    try {
      return await IdentityService.postPassword(data)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const postPasswordRecovery = createAsyncThunk(
  'identity/postPasswordRecovery',
  async (data, { rejectWithValue }) => {
    try {
      return await IdentityService.postPasswordRecovery(data)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const putPasswordRecovery = createAsyncThunk(
  'identity/putPasswordRecovery',
  async (data, { rejectWithValue }) => {
    try {
      return await IdentityService.putPasswordRecovery(data)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const resendVerify = createAsyncThunk(
  'identity/resendVerify',
  async (_, { rejectWithValue }) => {
    try {
      return await IdentityService.resendVerify()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

// export const signInWithGoogle = createAsyncThunk('identity/signInWithGoogle', async (_, { rejectWithValue }) => {
// 	try {
// 		const response = await IdentityService.loginInOAuth()
// 		const token = response.data.token;
// 		localStorage.setItem(AUTH_TOKEN, token);
// 		return token;
// 	} catch (err) {
// 		return rejectWithValue(err.response?.data?.error || 'Error')
// 	}
// })

// export const signInWithFacebook = createAsyncThunk('identity/signInWithFacebook', async (_, { rejectWithValue }) => {
// 	try {
// 		const response = await IdentityService.loginInOAuth()
// 		const token = response.data.token;
// 		localStorage.setItem(AUTH_TOKEN, token);
// 		return token;
// 	} catch (err) {
// 		return rejectWithValue(err.response?.data?.error || 'Error')
// 	}
// })

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false
      state.redirect = '/'
      state.token = action.payload
      state.roles = jwtDecode(action.payload).roles
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload
      state.showMessage = true
      state.loading = false
    },
    hideAuthMessage: state => {
      state.message = ''
      state.showMessage = false
    },
    signOutSuccess: state => {
      state.loading = false
      state.token = null
      state.redirect = '/'
    },
    showLoading: state => {
      state.loading = true
    },
    signInSuccess: (state, action) => {
      state.loading = false
      state.token = action.payload
      state.roles = jwtDecode(action.payload).roles
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getMe.pending, state => {
        state.loading = true
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false
        // state.redirect = '/'
        state.me = action.payload
      })
      .addCase(getMe.rejected, state => {
        // state.message = action.payload
        // state.showMessage = true
        state.loading = false
      })
      .addCase(updateMe.pending, state => {
        state.updateMeIsLoading = true
      })
      .addCase(updateMe.fulfilled, (state, action) => {
        state.updateMeIsLoading = false
        state.me = action.payload
        openSuccessNotification({
          key: 'updateMe',
          message: i18n.t('message.update.me.success'),
        })
      })
      .addCase(updateMe.rejected, (state, action) => {
        state.updateMeIsLoading = false
        openErrorNotification({
          key: 'updateMe',
          message: i18n.t(action.payload),
        })
      })
      .addCase(changePassword.pending, state => {
        state.changePasswordIsLoading = true
      })
      .addCase(changePassword.fulfilled, state => {
        state.changePasswordIsLoading = false
        openSuccessNotification({
          key: 'changepassword',
          message: i18n.t('message.update.password.success'),
        })
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changePasswordIsLoading = false
        openErrorNotification({
          key: 'changepassword',
          message: action.payload,
        })
      })
      .addCase(signIn.pending, state => {
        state.loading = true
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload
        state.roles = jwtDecode(action.payload).roles

        openSuccessNotification({
          message: i18n.t('message.signin.auth.success'),
          key: 'signIn',
        })
      })
      .addCase(signIn.rejected, (state, action) => {
        state.loading = false

        openErrorNotification({
          key: 'signIn',
          message: i18n.t(action.payload),
        })
      })
      .addCase(signUp.pending, state => {
        state.loading = true
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload
        state.roles = jwtDecode(action.payload).roles
        openSuccessNotification({
          key: 'signUp',
          message: i18n.t('message.signup.auth.success'),
        })
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = false

        openErrorNotification({
          key: 'signUp',
          message: i18n.t(action.payload),
        })
      })
      .addCase(postPasswordRecovery.pending, state => {
        state.loading = true
      })
      .addCase(postPasswordRecovery.fulfilled, state => {
        state.loading = false

        openSuccessNotification({
          key: 'passwordrecovery',
          message: i18n.t('message.passwordrecovery.auth.email.success'),
        })
      })
      .addCase(postPasswordRecovery.rejected, (state, action) => {
        state.loading = false

        openErrorNotification({
          key: 'passwordrecovery',
          message: action.payload,
        })
      })
      .addCase(putPasswordRecovery.pending, state => {
        state.loading = true
      })
      .addCase(putPasswordRecovery.fulfilled, state => {
        state.loading = false

        openSuccessNotification({
          key: 'passwordrecovery',
          message: i18n.t('message.passwordrecovery.auth.success'),
        })
      })
      .addCase(putPasswordRecovery.rejected, (state, action) => {
        state.loading = false

        openErrorNotification({
          key: 'passwordrecovery',
          message: action.payload,
        })
      })

      .addCase(resendVerify.pending, state => {
        state.resendVerifyIsLoading = true
      })
      .addCase(resendVerify.fulfilled, state => {
        state.resendVerifyIsLoading = false

        openSuccessNotification({
          key: 'resendVerify',
          message: i18n.t('message.resend.verify.success'),
        })
      })
      .addCase(resendVerify.rejected, (state, action) => {
        state.resendVerifyIsLoading = false

        openErrorNotification({
          key: 'resendVerify',
          message: action.payload,
        })
      })
    // .addCase(signInWithGoogle.pending, (state) => {
    // 	state.loading = true
    // })
    // .addCase(signInWithGoogle.fulfilled, (state, action) => {
    // 	state.loading = false
    // 	state.redirect = '/'
    // 	state.token = action.payload
    // })
    // .addCase(signInWithGoogle.rejected, (state, action) => {
    // 	state.message = action.payload
    // 	state.showMessage = true
    // 	state.loading = false
    // })
    // .addCase(signInWithFacebook.pending, (state) => {
    // 	state.loading = true
    // })
    // .addCase(signInWithFacebook.fulfilled, (state, action) => {
    // 	state.loading = false
    // 	state.redirect = '/'
    // 	state.token = action.payload
    // })
    // .addCase(signInWithFacebook.rejected, (state, action) => {
    // 	state.message = action.payload
    // 	state.showMessage = true
    // 	state.loading = false
    // })
  },
})

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
} = authSlice.actions

export default authSlice.reducer
